import { useState, useEffect } from 'react';
import { useTranslation, Notification, Theme, useSession, type NotificationTheme } from '@nextgen-web-framework/all';
import {
  AccountOrderDetailsProps,
  AccountDetail,
  AccountDetailRenewalStatusEnum,
} from '@commerce-profile-webcomponents/sdk';
import { getAccountData } from '../../utils/accountLifeCycleUtils';
import { useLocale, useFormatDate } from '../../utils/customHooks';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { RANDOM_SPONSOR_NAME } from '../../utils/Constants';
import styles from './AccountOrderDetails.module.scss';

const AccountOrderDetails = ({
  accountNumber,
  sponsor,
  showRenewalDate,
  showRenewalMessage,
  showDowngradeMessage,
  messageTheme = Theme.SUCCESS,
  internationalSponsor,
}: AccountOrderDetailsProps) => {
  const t = useTranslation();
  const locale = useLocale();
  const { formatDate } = useFormatDate();
  const { session } = useSession();
  const { aboNum } = session;
  const [formattedDate, setFormattedDate] = useState<string>('');
  const apiErrorHandler = useGlobalErrorHandler();

  useEffect(() => {
    if (showRenewalDate) {
      getExpirationDate();
    }
  }, []);

  const getExpirationDate = async () => {
    if (aboNum) {
      const accountDetail: AccountDetail = await getAccountData(aboNum, apiErrorHandler);
      const { expirationDate, renewalStatus } = accountDetail;

      // To display expiration date, add 1 year to the current expiration date if status is yet to updated.
      const newExpDate = new Date(String(expirationDate));
      if (renewalStatus !== AccountDetailRenewalStatusEnum.AlreadyRenewed) {
        newExpDate.setFullYear(newExpDate.getFullYear() + 1);
      }

      const formattedExpDate = formatDate(newExpDate.toISOString(), locale);
      setFormattedDate(formattedExpDate);
    }
  };

  const confirmationTitle = t('profile.AccountRenewal.Renewal.successfullTitle');
  const confirmatioNDesc = showRenewalDate
    ? t('profile.AccountRenewal.Renewal.successfullMessage', { date: formattedDate })
    : '';

  const downgradeTitle = t('profile.AccountRenewal.Downgrade.successfullTitle');
  const downgradeDesc = showRenewalDate
    ? t('profile.AccountRenewal.Downgrade.successfullMessage', { date: formattedDate })
    : '';

  return (
    <div data-testid="account-details-container" className={styles['account-details-container']}>
      <div data-testid="account-details-heading" className={styles['account-details-heading']}>
        {t('profile.AccountOrderDetails.accountOrderDetails')}
      </div>
      <div data-testid="account-number-container" className={styles['account-number-container']}>
        <div data-testid="account-number-label" className={styles['account-number-label']}>
          {t('profile.AccountOrderDetails.accountId')}
        </div>
        <div data-testid="account-number-value" className={styles['account-number-value']}>
          {accountNumber}
        </div>
      </div>
      {sponsor && (
        <div data-testid="sponsor-container" className={styles['sponsor-container']}>
          <div data-testid="sponsor-heading" className={styles['sponsor-heading']}>
            {t('profile.AccountOrderDetails.sponsorInfo')}
          </div>
          <div data-testid="sponsor-name-container" className={styles['sponsor-name-container']}>
            <div data-testid="sponsor-name-label" className={styles['sponsor-name-label']}>
              {t('profile.AccountOrderDetails.sponsorName')}
            </div>
            <div data-testid="sponsor-name-value" className={styles['sponsor-name-value']}>
              {sponsor.accountName !== RANDOM_SPONSOR_NAME
                ? sponsor.accountName
                : t('profile.AccountOrderDetails.sponsorNameRandom')}
            </div>
          </div>
          {sponsor.accountName !== RANDOM_SPONSOR_NAME && (
            <div data-testid="sponsor-number-container" className={styles['sponsor-number-container']}>
              <div data-testid="sponsor-number-label" className={styles['sponsor-number-label']}>
                {t('profile.AccountOrderDetails.sponsorNumber')}
              </div>
              <div data-testid="sponsor-number-value" className={styles['sponsor-number-value']}>
                {sponsor.accountNumber}
              </div>
            </div>
          )}
        </div>
      )}
      {internationalSponsor && (
        <div data-testid="sponsor-container" className={styles['sponsor-container']}>
          <div data-testid="sponsor-heading" className={styles['sponsor-heading']}>
            {t('profile.AccountOrderDetails.internationalSponsorInfo')}
          </div>
          <div data-testid="sponsor-name-container" className={styles['sponsor-name-container']}>
            <div data-testid="sponsor-name-label" className={styles['sponsor-name-label']}>
              {t('profile.AccountOrderDetails.sponsorName')}
            </div>
            <div data-testid="sponsor-name-value" className={styles['sponsor-name-value']}>
              {internationalSponsor?.accountName}
            </div>
          </div>
          <div data-testid="sponsor-number-container" className={styles['sponsor-number-container']}>
            <div data-testid="sponsor-number-label" className={styles['sponsor-number-label']}>
              {t('profile.AccountOrderDetails.sponsorNumber')}
            </div>
            <div data-testid="sponsor-number-value" className={styles['sponsor-number-value']}>
              {internationalSponsor?.accountNumber}
            </div>
          </div>
        </div>
      )}
      {(showRenewalMessage || showRenewalDate) && (
        <div className={styles['confirmation-message-container']} data-testid="confirmation-message-container">
          <Notification
            message={confirmationTitle}
            description={confirmatioNDesc}
            theme={messageTheme as NotificationTheme}
            hasBoldText={true}
            hasNotificationIcon={true}
            zIndex={1}
          />
        </div>
      )}
      {showDowngradeMessage && (
        <div
          className={styles['confirmation-message-container']}
          data-testid="downgrade-confirmation-message-container"
        >
          <Notification
            message={downgradeTitle}
            description={downgradeDesc}
            theme={Theme.WARNING}
            hasBoldText={true}
            hasNotificationIcon={true}
            zIndex={1}
          />
        </div>
      )}
    </div>
  );
};
export default AccountOrderDetails;
